import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SearchableDropdown from '../Components/SearchableDropdown';
import api from '../Utils/Axios';
import { useRegion } from '../context/RegionContext';
import {
  getRegionCountryOptions,
  getRegionCountryRecord,
  getRegionCityOptions,
  getRegionStateLabel,
  getRegionStateOptions,
  getRegionStateRecord,
} from '../Utils/region';

const FindJob = () => {
  const navigate = useNavigate();
  const [selectedState, setSelectedState] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [zipcode, setZipcode] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [categories, setCategories] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const { region } = useRegion();
  const categoryIcons = useMemo(
    () => [
      '/assets/images/icon/medical.svg',
      '/assets/images/icon/technology.svg',
      '/assets/images/icon/administration.svg',
      '/assets/images/icon/account-finance.svg',
      '/assets/images/icon/marketing.svg',
      '/assets/images/icon/education.svg',
      '/assets/images/icon/receptionist-06.svg',
      '/assets/images/icon/non-profit-org.svg',
      '/assets/images/icon/transportation.svg',
      '/assets/images/icon/architect.svg',
    ],
    []
  );
  const categoryIconMap = useMemo(
    () => ({
      nursing: '/assets/images/nursing.svg',
      nurse: '/assets/images/nursing.svg',
      'allied health': '/assets/images/allied-health.svg',
      'allied-health': '/assets/images/allied-health.svg',
      leadership: '/assets/images/leadership.svg',
      'support services': '/assets/images/support-service.svg',
      'support service': '/assets/images/support-service.svg',
      'support-service': '/assets/images/support-service.svg',
      physician: '/assets/images/physision.png',
      physision: '/assets/images/physision.png',
      doctors: '/assets/images/physision.png',
      doctor: '/assets/images/physision.png',
    }),
    []
  );

  const stateLabel = useMemo(() => getRegionStateLabel(region), [region]);
  const countryOptions = useMemo(() => (region === 'af' ? getRegionCountryOptions(region) : []), [region]);
  const selectedCountryRecord = useMemo(
    () => (region === 'af' ? getRegionCountryRecord(region, selectedCountry) : null),
    [region, selectedCountry]
  );
  const stateNames = useMemo(() => getRegionStateOptions(region, selectedCountryRecord), [region, selectedCountryRecord]);
  const selectedStateObj = useMemo(
    () => getRegionStateRecord(region, selectedState, selectedCountryRecord),
    [region, selectedState, selectedCountryRecord]
  );
  const cities = useMemo(
    () => getRegionCityOptions(region, selectedStateObj, selectedCountryRecord),
    [region, selectedStateObj, selectedCountryRecord]
  );

  useEffect(() => {
    const run = async () => {
      try {
        const [categoryRes, jobsRes] = await Promise.all([
          api.get('/api/categories'),
          api.get('/api/hospital/public-jobs'),
        ]);
        setCategories(categoryRes.data?.categories || []);
        setJobs(jobsRes.data?.jobs || []);
      } catch (error) {
        setCategories([]);
        setJobs([]);
      } finally {
        setLoading(false);
      }
    };

    run();
  }, []);

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoadingJobs(true);
    const params = new URLSearchParams();
    if (region === 'af' && selectedCountry) params.set('country', selectedCountry);
    if (selectedState) params.set('state', selectedState);
    if (selectedCity) params.set('city', selectedCity);
    if (zipcode) params.set('zip', zipcode);
    const query = params.toString();
    navigate(query ? `/job-listing?${query}` : '/job-listing');
  };

  const categoryCards = useMemo(() => {
    const counts = {};
    jobs.forEach((job) => {
      const key = (job?.category || '').trim().toLowerCase();
      if (!key) return;
      counts[key] = (counts[key] || 0) + 1;
    });

    return categories.map((category, index) => ({
      id: category._id || `${category.name}-${index}`,
      name: category.name,
      count: counts[(category.name || '').trim().toLowerCase()] || 0,
      icon:
        categoryIconMap[(category.name || '').trim().toLowerCase()] ||
        categoryIcons[index % categoryIcons.length],
      serial: String(index + 1).padStart(2, '0'),
    }));
  }, [jobs, categories, categoryIcons, categoryIconMap]);

  const getJobListLink = (categoryName = '') => {
    const params = new URLSearchParams();
    if (region === 'af' && selectedCountry) params.set('country', selectedCountry);
    if (selectedState) params.set('state', selectedState);
    if (selectedCity) params.set('city', selectedCity);
    if (zipcode) params.set('zip', zipcode);
    if (categoryName) params.set('category', categoryName);
    const query = params.toString();
    return query ? `/job-listing?${query}` : '/job-listing';
  };

  return (
    <div>
      <style>
        {`
          .home1-category .single-category .category-top .icon {
            width: 88px;
            height: 88px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border-radius: 20px;
            background: #ffffff;
            border: 1px solid rgba(201, 154, 24, 0.25);
            box-shadow: 0 10px 20px rgba(201, 154, 24, 0.18);
          }
          .home1-category .single-category .category-top .icon img {
            width: 56px;
            height: 56px;
            object-fit: contain;
          }
          .home1-category {
            background: linear-gradient(180deg, rgba(201, 154, 24, 0.08) 0%, rgba(201, 154, 24, 0.02) 100%);
          }
          .home1-category .single-category {
            border: 1px solid rgba(201, 154, 24, 0.22);
            box-shadow: 0 14px 30px rgba(21, 42, 80, 0.1);
          }
          .home1-category .single-category:hover {
            transform: translateY(-10px);
          }
        `}
      </style>
      <div className="inner-banner">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="banner-content text-center">
                <h1>Find Hospital Jobs</h1>
                <span />
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to="/">Home</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Find Jobs
                    </li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="job-listing-area pt-120 mb-120">
        <div className="container">
          <div className="row g-4 mb-40">
            <div className="col-12">
              <form onSubmit={handleSearch}>
                <div className="row g-3">
                  {region === 'af' ? (
                    <div className="col-lg-3 col-md-6">
                      <div className="form-inner" style={{ position: 'relative' }}>
                        <select
                          value={selectedCountry}
                          onChange={(e) => {
                            setSelectedCountry(e.target.value);
                            setSelectedState('');
                            setSelectedCity('');
                          }}
                          style={{
                            width: '100%',
                            height: '60px',
                            padding: '0 50px 0 20px',
                            background: '#fff',
                            border: '1px solid #DFE9E5',
                            borderRadius: '5px',
                            color: selectedCountry ? 'var(--title-color1)' : '#7C8A9B',
                            appearance: 'none',
                            WebkitAppearance: 'none',
                            MozAppearance: 'none',
                          }}
                        >
                          <option value="">Country</option>
                          {countryOptions.map((country) => (
                            <option key={country} value={country}>
                              {country}
                            </option>
                          ))}
                        </select>
                        <span
                          style={{
                            position: 'absolute',
                            right: '25px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            pointerEvents: 'none',
                          }}
                        >
                          <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                              d="M1.41 0.589966L6 5.16997L10.59 0.589966L12 1.99997L6 7.99997L0 1.99997L1.41 0.589966Z"
                              fill="#1C2120"
                            />
                          </svg>
                        </span>
                      </div>
                    </div>
                  ) : null}
                  <div className="col-lg-3 col-md-6">
                    <div className="form-inner" style={{ position: 'relative' }}>
                      <select
                        value={selectedState}
                        onChange={(e) => {
                          setSelectedState(e.target.value);
                          setSelectedCity('');
                        }}
                        disabled={region === 'af' && !selectedCountry}
                        style={{
                          width: '100%',
                          height: '60px',
                          padding: '0 50px 0 20px',
                          background: '#fff',
                          border: '1px solid #DFE9E5',
                          borderRadius: '5px',
                          color: selectedState ? 'var(--title-color1)' : '#7C8A9B',
                          appearance: 'none',
                          WebkitAppearance: 'none',
                          MozAppearance: 'none',
                          opacity: region === 'af' && !selectedCountry ? 0.6 : 1,
                          cursor: region === 'af' && !selectedCountry ? 'not-allowed' : 'pointer',
                        }}
                      >
                        <option value="">{stateLabel}</option>
                        {stateNames.map((state) => (
                          <option key={state} value={state}>
                            {state}
                          </option>
                        ))}
                      </select>
                      <span
                        style={{
                          position: 'absolute',
                          right: '25px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          pointerEvents: 'none',
                        }}
                      >
                        <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M1.41 0.589966L6 5.16997L10.59 0.589966L12 1.99997L6 7.99997L0 1.99997L1.41 0.589966Z" fill="#1C2120" />
                        </svg>
                      </span>
                    </div>
                  </div>
                  <div className="col-lg-3 col-md-6">
                    <div className="form-inner" style={{ position: 'relative' }}>
                      <select
                        value={selectedCity}
                        onChange={(e) => setSelectedCity(e.target.value)}
                        disabled={!selectedState}
                        style={{
                          width: '100%',
                          height: '60px',
                          padding: '0 50px 0 20px',
                          background: '#fff',
                          border: '1px solid #DFE9E5',
                          borderRadius: '5px',
                          color: selectedCity ? 'var(--title-color1)' : '#7C8A9B',
                          appearance: 'none',
                          WebkitAppearance: 'none',
                          MozAppearance: 'none',
                          opacity: !selectedState ? 0.6 : 1,
                          cursor: !selectedState ? 'not-allowed' : 'pointer',
                        }}
                      >
                        <option value="">City</option>
                        {cities.map((city) => (
                          <option key={city} value={city}>
                            {city}
                          </option>
                        ))}
                      </select>
                      <span
                        style={{
                          position: 'absolute',
                          right: '25px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          pointerEvents: 'none',
                        }}
                      >
                        <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M1.41 0.589966L6 5.16997L10.59 0.589966L12 1.99997L6 7.99997L0 1.99997L1.41 0.589966Z" fill="#1C2120" />
                        </svg>
                      </span>
                    </div>
                  </div>
                  <div className="col-lg-3 col-md-6">
                    <div className="form-inner">
                      <input
                        type="text"
                        placeholder="ZIP"
                        value={zipcode}
                        onChange={(e) => setZipcode(e.target.value.replace(/\D/g, ''))}
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={10}
                      />
                    </div>
                  </div>
                  <div className="col-lg-3 col-md-6">
                    <button className="primry-btn-2 lg-btn w-100" type="submit" disabled={loadingJobs}>
                      {loadingJobs ? 'Searching...' : 'Search'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>

           <div className="home1-category pt-120 mb-120">
                 <div className="container">
                   <div className="row mb-60">
                     <div className="col-12 d-flex flex-wrap align-items-end justify-content-md-between justify-content-start gap-3">
                       <div className="section-title1">
                         <h2>
                           Job <span>Categories</span>
                         </h2>
                         <p>Dynamic categories from admin panel with live hiring activity.</p>
                       </div>
                       <div className="explore-btn">
                         <Link to="/find-job">
                           Explore More{" "}
                           <span>
                             <img src="assets/images/icon/explore-elliose.svg" alt="" />
                           </span>
                         </Link>
                       </div>
                     </div>
                   </div>
                   <div className="row row-cols-xxl-5 row-cols-xl-4 row-cols-md-3 row-cols-sm-2 row-cols-1 g-3 cf justify-content-center">
                     {categoryCards.length === 0 ? (
                       <div className="col-12">
                         <div className="single-category" style={{ textAlign: 'center' }}>
                           <div className="category-content">
                             <h5>No category available yet.</h5>
                             <p>Add categories from admin panel to show here.</p>
                           </div>
                         </div>
                       </div>
                     ) : (
                       categoryCards.map((category) => (
                       <div className="col" key={category.id}>
                           <Link
                             to={getJobListLink(category.name)}
                             className="single-category"
                             style={{
                               display: 'block',
                               border: '1px solid #e7ecf3',
                               borderRadius: 12,
                               background: 'linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)',
                               boxShadow: '0 10px 25px rgba(21, 42, 80, 0.06)',
                             }}
                           >
                             <div className="category-top">
                               <div className="icon">
                                 <img src={category.icon} alt={category.name} />
                               </div>
                               <div className="sl-no">
                                 <h6>{category.serial}</h6>
                               </div>
                             </div>
                             <div className="category-content">
                               <h5>{category.name}</h5>
                               <p>
                                 Job Available: <span>{category.count}</span>
                               </p>
                             </div>
                           </Link>
                         </div>
                       ))
                     )}
                   </div>
                 </div>
               </div>

         
        </div>
      </div>
    </div>
  );
};

export default FindJob;
